(() => {
  if (window.mcAutoSizeGuideInitialized) return;
  window.mcAutoSizeGuideInitialized = true;

  const chartLabel = /\b(?:size\s*(?:chart|guide|table)|sizing\s*(?:chart|guide|table)|measurement\s*(?:chart|guide)|maat\s*(?:tabel|gids)|maattabel|größen?\s*tabelle|groessen?\s*tabelle|guide\s*des\s*tailles|tabla\s*de\s*tallas)\b/i;
  const headingLabel = /^(?:(?:women'?s?|men'?s?|kids'?|our|your)\s+)?(?:size(?:s|\s*(?:chart|guide|table))?|sizing(?:\s*(?:chart|guide|table))?|measurements?|maat\s*(?:tabel|gids)|maattabel|größen?\s*tabelle|groessen?\s*tabelle|guide\s*des\s*tailles|tabla\s*de\s*tallas)(?:\s*[:\-–(].*)?$/i;
  const normalize = value => String(value || '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

  function isMarked(node) {
    return chartLabel.test(normalize([
      node.getAttribute('alt'), node.getAttribute('title'),
      node.getAttribute('aria-label'), node.id, node.className,
      node.getAttribute('src'), node.getAttribute('data-src')
    ].join(' '))) || node.hasAttribute('data-size-chart');
  }

  function hasChartHeading(node, description) {
    const isChartHeading = heading => {
      const text = normalize(heading.textContent);
      return headingLabel.test(text) && (/^H[1-6]$/.test(heading.tagName) || chartLabel.test(text) || /^measurements?\s*[:(]?/i.test(text));
    };
    const headings = Array.from(description.querySelectorAll('h1,h2,h3,h4,h5,h6,p,strong,b,figcaption')).filter(heading =>
      /^H[1-6]$/.test(heading.tagName) || isChartHeading(heading)
    );
    const preceding = headings.filter(heading =>
      !heading.contains(node) && (heading.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
    ).pop();
    return !!preceding && isChartHeading(preceding);
  }

  function isSizeTable(table, description) {
    const rows = Array.from(table.querySelectorAll('tr,[role="row"]'));
    if (rows.length < 2) return false;
    const cells = rows.flatMap(row => Array.from(row.querySelectorAll('th,td,[role="columnheader"],[role="cell"]')));
    if (cells.length < 4) return false;
    if (isMarked(table) || hasChartHeading(table, description)) return true;
    const headings = rows.slice(0, 2).flatMap(row => Array.from(row.querySelectorAll('th,td,[role="columnheader"],[role="cell"]')).map(cell => normalize(cell.textContent))).join(' ');
    const hasSize = /\b(?:sizes?|sizing|maat|maten|tallas?|taille|us|uk|eu)\b/i.test(headings);
    const hasMeasurements = /\b(?:bust|chest|waist|hips?|shoulder|sleeve|inseam|length|foot|cm|inches?|inch|in)\b/i.test(headings);
    const apparelSizes = rows.filter(row => /^(?:[2-6]?X[SL]|XX[SL]|XS|S|M|L|XL|XXL|XXXL)$/i.test(normalize(row.querySelector('td,[role="cell"]')?.textContent))).length >= 2;
    return hasSize || (hasMeasurements && apparelSizes);
  }

  function cleanClone(source) {
    const clone = source.cloneNode(true);
    clone.querySelectorAll('script,style,iframe,object,embed,form,input,button').forEach(node => node.remove());
    [clone, ...clone.querySelectorAll('*')].forEach(node => {
      Array.from(node.attributes).forEach(attribute => {
        if (attribute.name === 'id' || attribute.name === 'style' || attribute.name === 'width' || attribute.name === 'height' || attribute.name.startsWith('on')) node.removeAttribute(attribute.name);
      });
    });
    if (clone.tagName === 'IMG') {
      const url = [source.currentSrc, source.getAttribute('src'), source.getAttribute('data-src')].find(value => value && !value.startsWith('data:') && !value.includes('{width}'));
      if (!url) return null;
      clone.src = url;
      clone.loading = 'lazy';
      clone.decoding = 'async';
      clone.sizes = '100vw';
    }
    return clone;
  }

  function prepare(scope = document) {
    const descriptions = Array.from(scope.querySelectorAll('.mc-product-description'));
    if (scope.matches?.('.mc-product-description')) descriptions.unshift(scope);
    descriptions.forEach(description => {
      const section = description.closest('.product-section') || description.closest('section');
      if (!section) return;
      const id = description.dataset.mcProductDescription;
      const dialog = section.querySelector('[id="mc-size-guide-' + id + '"][data-mc-auto-size-guide-dialog]');
      const triggers = Array.from(section.querySelectorAll('[data-mc-auto-size-guide-trigger]')).filter(trigger => trigger.getAttribute('aria-controls') === dialog?.id);
      if (!dialog || !triggers.length) return;
      const content = dialog.querySelector('[data-mc-size-guide-content]');
      if (!content) return;
      const meta = description.closest('.product-single__meta') || section;
      const dualGuide = meta.querySelector('.mc-inline-size-table[data-mc-dual-size-guide]');
      const dualSources = dualGuide ? ['inches', 'cm'].map(unit =>
        dualGuide.querySelector('[data-mc-size-guide-source="' + unit + '"] table')
      ) : [];
      const hasDualSources = dualSources.length === 2 && dualSources.every(Boolean);
      const metafield = meta.querySelector('.mc-inline-size-table table');
      const tables = Array.from(description.querySelectorAll('table,[role="table"]')).filter(table =>
        !table.parentElement.closest('table,[role="table"]') && isSizeTable(table, description)
      );
      const images = Array.from(description.querySelectorAll('img')).filter(image => isMarked(image) || hasChartHeading(image, description));
      let sources = hasDualSources ? dualSources : metafield ? [metafield] : [...tables, ...images];
      if (!sources.length) sources = Array.from(section.querySelectorAll('img')).filter(isMarked);
      const seen = new Set();
      const clones = sources.filter(source => {
        const key = source.tagName === 'IMG' ? (source.getAttribute('data-src') || source.getAttribute('src') || '') .split('?')[0] : source;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }).map(cleanClone).filter(Boolean);
      if (hasDualSources && clones.length === 2) {
        const unitSwitch = document.createElement('div');
        unitSwitch.className = 'mc-size-guide-unit-switch';
        unitSwitch.setAttribute('role', 'group');
        unitSwitch.setAttribute('aria-label', 'Size guide units');

        const tablePanels = clones.map((table, index) => {
          const unit = index === 0 ? 'inches' : 'cm';
          const panel = document.createElement('div');
          panel.className = 'mc-size-guide-unit-table';
          panel.dataset.mcSizeGuideUnitTable = unit;
          panel.hidden = index !== 0;
          panel.appendChild(table);

          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = unit === 'inches' ? 'Inches' : 'cm';
          button.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');
          button.dataset.mcSizeGuideUnit = unit;
          if (index === 0) button.classList.add('is-active');
          unitSwitch.appendChild(button);
          return panel;
        });
        content.replaceChildren(unitSwitch, ...tablePanels);
      } else {
        content.replaceChildren(...clones);
      }
      const hasChart = clones.length > 0;
      triggers.forEach(trigger => {
        trigger.hidden = !hasChart;
        const fallback = trigger.parentElement.querySelector('[data-mc-auto-size-guide-fallback]');
        if (fallback) fallback.hidden = hasChart;
      });
      dialog.dataset.mcSizeGuideReady = String(hasChart);
    });
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-mc-size-guide-unit]');
    if (!button) return;
    const dialog = button.closest('[data-mc-auto-size-guide-dialog]');
    if (!dialog) return;
    const selectedUnit = button.dataset.mcSizeGuideUnit;
    dialog.querySelectorAll('[data-mc-size-guide-unit]').forEach(item => {
      const active = item === button;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    dialog.querySelectorAll('[data-mc-size-guide-unit-table]').forEach(panel => {
      panel.hidden = panel.dataset.mcSizeGuideUnitTable !== selectedUnit;
    });
  });

  document.addEventListener('shopify:section:load', event => prepare(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => prepare(), { once: true });
  else prepare();
})();
